<template>
  <div class="movie-detail-page">
    <!-- 头部导航 -->
    <header class="header">
      <div class="nav-left">
        <div class="back-btn el-icon-arrow-left" @click="goBack"></div>
      </div>
      <div class="nav-center">
        <h1 class="page-title">电影详情</h1>
      </div>
    </header>

    <!-- 电影信息区域 -->
    <div class="movie-info-section">
      <div class="movie-poster">
        <img :src="movieInfo.poster" :alt="movieInfo.title" />
      </div>
      <div class="movie-details">
        <h2 class="movie-title">{{ movieInfo.title }}</h2>
        <div class="movie-meta">
          <span class="region">{{ movieInfo.region }}</span>
          <span class="separator">|</span>
          <span class="genre">{{ movieInfo.genre }}</span>
          <span class="separator">|</span>
          <span class="duration">{{ movieInfo.duration }}</span>
        </div>
        <div class="director">
          <span class="label">导演:</span>
          <span class="value">{{ movieInfo.director }}</span>
        </div>
        <div class="cast">
          <span class="label">主演:</span>
          <span class="value">{{ movieInfo.cast }}</span>
        </div>
      </div>
    </div>

    <!-- 剧情简介 -->
    <div class="synopsis-section">
      <h3 class="section-title">剧情简介</h3>
      <div class="synopsis-content">
        <p class="synopsis-text" :class="{ 'expanded': isExpanded }">{{ movieInfo.synopsis }}</p>
        <button class="expand-btn" @click="toggleExpanded">
          <span class="expand-text">{{ isExpanded ? '收起' : '展开' }}</span>
          <span class="expand-icon" :class="{ 'rotated': isExpanded }">{{ isExpanded ? '▲' : '▼' }}</span>
        </button>
      </div>
    </div>

    <!-- 选择场次 -->
    <div class="showtime-section">
      <h3 class="section-title">选择场次</h3>

      <div>
        <!-- 日期选择 -->
        <div class="date-selector">
          <button
            v-for="(date, index) in dates"
            :key="index"
            :class="['date-btn', { active: selectedDate === index }]"
            @click="selectDate(index)"
          >{{ date.text }}</button>
        </div>

        <!-- 场次列表 -->
        <div class="showtime-list">
          <div v-for="(showtime, index) in showtimes" :key="index" class="showtime-item">
            <div class="session-name">{{showtime.sessionName}}</div>
            <div class="showtime-main-row">
              <div class="showtime-info">
                <div class="time-info">
                  <span class="start-time">{{ showtime.startTime }}</span>
                  <span class="end-info">散场 {{ showtime.endTime }}</span>
                </div>
              </div>
              <div class="remaining-tickets">余票：{{showtime.remainingVotesForEachSession}}</div>
              <div class="ticket-selector">
                <button
                  class="ticket-btn minus"
                  @click="decreaseTickets(index)"
                  :disabled="showtime.tickets <= 0 || isShowtimeInPast(showtime)"
                >−</button>
                <span class="ticket-count">{{ showtime.tickets }}</span>
                <button
                  class="ticket-btn plus"
                  :disabled="totalTickets>=movieShowtimes||showtime.remainingVotesForEachSession<=showtime.tickets || isShowtimeInPast(showtime)"
                  @click="increaseTickets(index)"
                >+</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 底部操作栏 -->
    <div class="bottom-bar">
      <div class="total-info">
        <span class="total-text">合计: {{ totalTickets }}张</span>
      </div>
      <button
        class="confirm-btn"
        :disabled="totalTickets==0|| isConfirmButtonDisabled"
        @click="confirmSelection"
      >确认选择</button>
    </div>

    <!-- 预约数不足弹窗 -->
    <div v-if="showInsufficientDialog" class="dialog-overlay">
      <div class="dialog-container" @click.stop>
        <div class="dialog-header">
          <h3 class="dialog-title">预约数不足</h3>
          <button class="close-btn" @click="closeInsufficientDialog">×</button>
        </div>
        <div class="dialog-content">
          <div class="warning-icon">⚠️</div>
          <p class="dialog-message">很抱歉，您选择的场次预约数不足，请选择其他场次或减少预约数量。</p>
          <div class="insufficient-info">
            <div v-for="item in insufficientItems" :key="item.id" class="insufficient-item">
              <div class="ticket-info">
                <div class="ticket-info-item">
                  <span class="date">{{ item.date }}</span>
                  <span class="time">{{ item.startTime }} - {{ item.endTime }}</span>
                  <span class="selected">已选: {{ item.selectedTickets }}张</span>
                  <span class="available">可预约: {{ item.availableTickets }}张</span>
                </div>
                <div class="ticket-info-msg">{{item.msg}}</div>
              </div>
            </div>
          </div>
        </div>
        <div class="dialog-footer">
          <button class="dialog-btn confirm" @click="closeInsufficientDialog">我知道了</button>
        </div>
      </div>
    </div>

    <!-- 观影人信息选择弹窗 -->
    <div v-if="showAudienceDialog" class="dialog-overlay">
      <div class="dialog-container" @click.stop>
        <div class="dialog-header">
          <h3 class="dialog-title">选择观影人</h3>
          <button class="close-btn" @click="cancelAudienceSelection">×</button>
        </div>
        <div class="dialog-content">
          <div class="audience-icon">👥</div>
          <p class="dialog-message" style="color:#C34D34;font-weight:bold;">添加观影人请到“我的-观影人”添加</p>
          <p class="dialog-message">请选择观影人信息（需要选择{{ totalTickets }}人）：</p>

          <!-- 观影人列表 -->
          <div class="audience-list">
            <div
              v-for="(audience, index) in audienceData.audiences"
              :key="index"
              class="audience-item"
              :class="{ 
                selected: audienceData.selectedAudiences.includes(index),
                disabled: isAudienceDisabled(index)
              }"
              @click="toggleAudienceSelection(index)"
            >
              <div class="audience-info">
                <div class="audience-name">{{ audience.name }}</div>
                <div class="audience-phone">{{ audience.phone }}</div>
              </div>
              <div class="audience-checkbox">
                <span v-if="audienceData.selectedAudiences.includes(index)" class="check-icon">✓</span>
              </div>
            </div>
          </div>

          <!-- 空状态 -->
          <div class="empty-state" v-if="audienceData.audiences.length === 0">
            <div class="empty-icon el-icon-user-solid"></div>
            <div class="empty-text">暂无观影人</div>
          </div>
          <!-- 选择提示 -->
          <div
            class="selection-hint"
            :class="{ 'selection-complete': audienceData.selectedAudiences.length === totalTickets }"
          >
            已选择 {{ audienceData.selectedAudiences.length }} / {{ totalTickets }} 人
            <span
              v-if="audienceData.selectedAudiences.length === totalTickets"
              class="complete-text"
            >✓ 选择完成</span>
          </div>
        </div>
        <div class="dialog-footer">
          <button class="dialog-btn cancel" @click="cancelAudienceSelection">取消</button>
          <button
            class="dialog-btn confirm"
            @click="confirmAudienceSelection"
            :disabled="audienceData.selectedAudiences.length !== totalTickets"
          >确定</button>
        </div>
      </div>
    </div>

    <!-- 确认选择弹窗 -->
    <div v-if="showConfirmDialog" class="dialog-overlay">
      <div class="dialog-container" @click.stop>
        <div class="dialog-header">
          <h3 class="dialog-title">确认选择</h3>
          <button class="close-btn" @click="cancelConfirmation">×</button>
        </div>
        <div class="dialog-content">
          <div class="confirm-icon">🎬</div>
          <p class="dialog-message">请确认您的选择：</p>
          <div class="confirm-info">
            <div class="confirm-summary">
              <div class="summary-item">
                <span class="summary-label">电影：</span>
                <span class="summary-value">{{ movieInfo.title }}</span>
              </div>
              <div class="summary-item">
                <span class="summary-label">总票数：</span>
                <span class="summary-value">{{ confirmData.totalTickets }}张</span>
              </div>
            </div>

            <div class="selected-details">
              <h4 class="details-title">选择详情：</h4>
              <div v-for="date in confirmData.selectedDates" :key="date.date" class="date-group">
                <div class="date-header">{{ date.dateText }}</div>
                <div class="session-list">
                  <div
                    v-for="ticket in confirmData.selectedTickets.filter(t => t.date === date.date)"
                    :key="ticket.id"
                    class="session-item"
                  >
                    <div class="session-info">
                      <span class="session-name">{{ ticket.sessionName }}</span>
                      <span class="session-time">{{ ticket.startTime }} - {{ ticket.endTime }}</span>
                    </div>
                    <div class="ticket-count">{{ ticket.tickets }}张</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="dialog-footer">
          <button class="dialog-btn cancel" @click="cancelConfirmation">取消</button>
          <button class="dialog-btn confirm" @click="proceedWithConfirmation">确认</button>
        </div>
      </div>
    </div>

    <!-- 页面加载组件 -->
    <div v-if="isLoading" class="page-loading-overlay">
      <div class="page-loading-container">
        <!-- 旋转的圆圈动画 -->
        <div class="page-spinner">
          <div class="page-spinner-inner"></div>
        </div>

        <!-- 加载文字 -->
        <div class="page-loading-text">
          <span class="page-loading-dots">
            <span class="page-dot">.</span>
            <span class="page-dot">.</span>
            <span class="page-dot">.</span>
          </span>
        </div>

        <!-- 可选的加载提示文字 -->
        <div v-if="loadingText" class="page-loading-message">{{ loadingText }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/utils/api";
import { getImageFromBinary, revokeImageURLs } from "@/utils/imageUtils";
export default {
  name: "MovieDetail",
  data() {
    return {
      selectedDate: 0,
      isExpanded: false,
      movieShowtimes: 0,
      showInsufficientDialog: false,
      insufficientItems: [],
      // 确认选择弹窗
      showConfirmDialog: false,
      confirmData: {
        selectedTickets: [],
        totalTickets: 0,
        selectedDates: []
      },
      // 观影人信息选择弹窗
      showAudienceDialog: false,
      audienceData: {
        audiences: [], // 观影人列表
        selectedAudiences: [], // 选中的观影人索引
        selectedAudienceIds: [] // 选中的观影人ID
      },
      // 页面加载状态
      isLoading: false,
      loadingText: "加载中，请稍等",
      // 按钮禁用状态
      isConfirmButtonDisabled: false,
      // 电影信息数据
      movieInfo: {
        title: "",
        poster: "",
        region: "",
        genre: "",
        duration: "",
        director: "",
        cast: "",
        synopsis: ""
      },
      dates: [],
      showtimes: [],
      AllTimePeriods: {}
    };
  },
  computed: {
    totalTickets() {
      // 计算所有日期的总票数总和
      let total = 0;
      for (const dateKey in this.AllTimePeriods) {
        if (this.AllTimePeriods[dateKey]) {
          total += this.AllTimePeriods[dateKey].reduce(
            (dateTotal, showtime) => dateTotal + showtime.tickets,
            0
          );
        }
      }
      return total;
    }
  },
  async mounted() {
    this.isLoading = true;
    try {
      await Promise.all([
        this.toTop(),
        this.getMovieDetails(),
        this.getMovieScreenings(),
        this.getUserInfo(),
        this.searchForViewerInformation()
      ]);
    } catch (error) {
      this.$message.error("接口请求出错");
    } finally {
      this.isLoading = false;
    }
  },
  activated() {
    // 确保被 keep-alive 激活时也回到顶部
    this.toTop();
  },
  beforeRouteEnter(to, from, next) {
    // 进入路由时回到顶部
    next(vm => vm.toTop());
  },
  methods: {
    async searchForViewerInformation() {
      try {
        let data = {
          openid: localStorage.getItem("openid"),
          clientsessionkey: localStorage.getItem("clientSessionKey")
        };
        const response = await api.SearchForViewerInformation(data);
        if (response && response.length > 0) {
          this.audienceData.audiences = response.map(item => {
            return {
              id: item.ID,
              name: item.ZuYuanXingMing,
              phone: item.LianXiDianHua
            };
          });
        }
      } catch (error) {
        console.error("查询观影人失败:", error);
        this.$message && this.$message.error("查询观影人失败");
      }
    },
    async getMovieScreenings() {
      try {
        const formData = new FormData();
        formData.append("id", this.$route.query.movieId);
        formData.append("days", "356");
        await api.MovieShowtimesFormData(formData).then(response => {
          if (response) {
            console.log(response);
            this.AllTimePeriods = this.convertData(response);
            // console.log(this.AllTimePeriods);
            this.generateDates();
          }
        });
      } catch (error) {
        console.error("获取电影场次失败:", error);
        this.$message && this.$message.error("获取电影场次失败");
      }
    },
    async getMovieDetails() {
      try {
        await api
          .GetMovieDetails(this.$route.query.movieId)
          .then(async response => {
            if (response && response.Data && response.Code == 200) {
              this.movieInfo = {
                title: response.Data.MingCheng,
                poster: await this.GetFile(response.Data.FileAttachmentId),
                region: response.Data.DiQu,
                genre: response.Data.LeiXing,
                duration: response.Data.ShiChang,
                director: response.Data.DaoYan,
                cast: response.Data.ZhuYan,
                synopsis: response.Data.JianJie
              };
            }
          });
      } catch (error) {
        console.error("获取电影详情失败:", error);
        this.$message && this.$message.error("获取电影详情失败！");
      }
    },
    async GetFile(id) {
      return await getImageFromBinary(id);
    },
    convertData(AllTimePeriods) {
      try {
        const inputData =
          typeof AllTimePeriods === "string"
            ? JSON.parse(AllTimePeriods)
            : AllTimePeriods;

        const result = {};

        for (const [date, sessions] of Object.entries(inputData)) {
          const dateParts = date.split("-");
          const shortDate = `${dateParts[1]}-${dateParts[2]}`;

          result[shortDate] = sessions.map(session => {
            const startTime = session.KaiShiShiJian.split(" ")[1];
            const startTimeShort = startTime.substring(0, 5);

            const endTime = session.JieSuShiJian.split(" ")[1];
            const endTimeShort = endTime.substring(0, 5);
            return {
              id: session.ID,
              startTime: startTimeShort,
              endTime: endTimeShort,
              tickets: 0,
              sessionName: session.MingCheng,
              fullDate: date,
              remainingVotesForEachSession:
                Number(session.YunXuRenShu - session.YiYuYueRenShu) < 0
                  ? 0
                  : Number(session.YunXuRenShu - session.YiYuYueRenShu)
            };
          });
        }
        return result;
      } catch (error) {
        console.error("数据转换错误:", error);
        this.$message && this.$message.error("数据转换失败");
      }
    },
    generateDates() {
      // 生成日期选择器数据
      this.dates = Object.keys(this.AllTimePeriods).map((shortDate, index) => {
        const [month, day] = shortDate.split("-");

        return {
          text: `${month}月${day}日`,
          shortDate: shortDate,
          index: index
        };
      });

      // 默认选择第一个日期
      if (this.dates.length > 0) {
        this.selectDate(0);
      }
    },
    AnalysisTime(timeString) {
      // 解析时间字符串，提取 HH:MM 格式
      if (!timeString) return "";

      const timePart = timeString.split(" ")[1];
      return timePart ? timePart.substring(0, 5) : "";
    },
    toTop() {
      const doScrollTop = () => {
        if (
          typeof window !== "undefined" &&
          typeof window.scrollTo === "function"
        ) {
          window.scrollTo(0, 0);
        }
        if (document && document.documentElement) {
          document.documentElement.scrollTop = 0;
        }
        if (document && document.body) {
          document.body.scrollTop = 0;
        }
      };
      // 在移动端渲染完成后再滚动，提升命中率
      if (
        typeof window !== "undefined" &&
        typeof window.requestAnimationFrame === "function"
      ) {
        window.requestAnimationFrame(() => {
          setTimeout(doScrollTop, 0);
        });
      } else {
        setTimeout(doScrollTop, 0);
      }
    },
    goBack() {
      this.$router.go(-1);
    },
    selectDate(index) {
      this.selectedDate = index;

      // 获取选中的日期
      const selectedDateKey = this.dates[index].shortDate;

      // 从转换后的数据中获取场次信息
      if (this.AllTimePeriods[selectedDateKey]) {
        this.showtimes = this.AllTimePeriods[selectedDateKey];
      } else {
        this.showtimes = [];
        console.warn("未找到该日期的场次数据:", selectedDateKey);
      }
    },
    increaseTickets(index) {
      if (this.totalTickets < this.movieShowtimes) {
        this.showtimes[index].tickets++;
      }
    },
    decreaseTickets(index) {
      if (this.showtimes[index].tickets > 0) {
        this.showtimes[index].tickets--;
      }
    },
    isShowtimeInPast(showtime) {
      try {
        if (!showtime || !showtime.fullDate || !showtime.startTime)
          return false;
        const start = new Date(`${showtime.fullDate} ${showtime.startTime}:00`);
        const now = new Date();
        return start.getTime() < now.getTime();
      } catch (e) {
        return false;
      }
    },
    async getUserInfo() {
      try {
        let data={
          openid: localStorage.getItem("openid"),
          clientsessionkey: localStorage.getItem("clientSessionKey")
        }
        await api.GetUserInfo(data).then(response => {
          this.movieShowtimes = Number(response);
          localStorage.setItem("movieShowtimes", Number(response));
        });
      } catch (error) {
        console.error("获取个人剩余场次失败:", error);
        this.$message && this.$message.error("获取个人剩余场次失败");
      }
    },
    async confirmSelection() {
      // 收集所有日期中每个时间段的 id 以及票数
      const selectedTickets = [];
      const selectedDates = [];

      for (const dateKey in this.AllTimePeriods) {
        if (this.AllTimePeriods[dateKey]) {
          this.AllTimePeriods[dateKey].forEach(showtime => {
            if (showtime.tickets > 0) {
              selectedTickets.push({
                id: showtime.id,
                tickets: showtime.tickets,
                startTime: showtime.startTime,
                endTime: showtime.endTime,
                date: dateKey,
                sessionName: showtime.sessionName
              });

              // 收集选中的日期信息
              if (!selectedDates.find(d => d.date === dateKey)) {
                selectedDates.push({
                  date: dateKey,
                  dateText:
                    this.dates.find(d => d.shortDate === dateKey)?.text ||
                    dateKey
                });
              }
            }
          });
        }
      }

      // 准备确认弹窗数据
      this.confirmData = {
        selectedTickets: selectedTickets,
        totalTickets: this.totalTickets,
        selectedDates: selectedDates
      };

      // 显示观影人选择弹窗
      this.showAudienceDialog = true;
    },

    async proceedWithConfirmation() {
      this.isLoading = true;
      this.isConfirmButtonDisabled = true;

      const selectedTickets = this.confirmData.selectedTickets;
      try {
        var data = {
          openid: localStorage.getItem("openid"),
          clientsessionkey: localStorage.getItem("clientSessionKey"),
          yuyues: selectedTickets.map(item => {
            return {
              id: item.id,
              num: item.tickets
            };
          }),
          zuyuans: this.audienceData.selectedAudienceIds.join(",")
        };
        await api.MovieReservation(data).then(response => {
          console.log(response);
          if (
            response.Code == 200 &&
            response.Data.length == 0 &&
            !("Msg" in response)
          ) {
            this.$message && this.$message.success("预约成功");
            setTimeout(() => {
              this.$router.push("/appointment-records");
            }, 1000);
          } else {
            // 预约数不足的情况
            this.showInsufficientDialog = true;
            this.insufficientItems = this.getInsufficientItems(response.Data);
          }
        });
      } catch (error) {
        console.error("预约失败:", error);
        this.$message && this.$message.error("预约失败");
      } finally {
        this.isLoading = false;
        this.isConfirmButtonDisabled = false;
        this.showConfirmDialog = false;
      }
    },

    cancelConfirmation() {
      this.showConfirmDialog = false;
      this.confirmData = {
        selectedTickets: [],
        totalTickets: 0,
        selectedDates: []
      };
    },
    getInsufficientItems(selectedTickets) {
      return selectedTickets.map(item => ({
        date: item.Date,
        startTime: item.starttime,
        endTime: item.endtime,
        selectedTickets: item.yixuanshu,
        availableTickets: item.keyueshu,
        msg: item.msg
      }));
    },
    async closeInsufficientDialog() {
      this.showInsufficientDialog = false;
      this.insufficientItems = [];
      this.isLoading = true;
      try {
        await Promise.all([
          this.toTop(),
          this.getMovieDetails(),
          this.getMovieScreenings(),
          this.getUserInfo()
        ]);
      } catch (error) {
        this.$message.error("接口请求出错");
      } finally {
        this.isLoading = false;
      }
    },
    toggleExpanded() {
      this.isExpanded = !this.isExpanded;
    },
    beforeDestroy() {
      const imageUrls = this.movieInfo.poster;
      revokeImageURLs(imageUrls);
    },
    // 观影人信息选择相关方法
    isAudienceDisabled(index) {
      // 如果已经选中，不禁用
      if (this.audienceData.selectedAudiences.includes(index)) {
        return false;
      }
      // 如果选择人数已达到票数要求，禁用未选择的项
      return this.audienceData.selectedAudiences.length >= this.totalTickets;
    },
    toggleAudienceSelection(index) {
      // 如果该项被禁用且未选中，则不允许选择
      if (
        this.isAudienceDisabled(index) &&
        !this.audienceData.selectedAudiences.includes(index)
      ) {
        return;
      }

      const selectedIndex = this.audienceData.selectedAudiences.indexOf(index);
      if (selectedIndex > -1) {
        // 如果已选中，则取消选择
        this.audienceData.selectedAudiences.splice(selectedIndex, 1);
      } else {
        // 如果未选中，则添加选择
        this.audienceData.selectedAudiences.push(index);
      }
    },
    cancelAudienceSelection() {
      this.showAudienceDialog = false;
      this.audienceData.selectedAudiences = [];
      this.audienceData.selectedAudienceIds = [];
    },
    confirmAudienceSelection() {
      if (this.audienceData.selectedAudiences.length !== this.totalTickets) {
        this.$message &&
          this.$message.warning(`请选择${this.totalTickets}位观影人`);
        return;
      }

      // 获取选中观影人的ID
      this.audienceData.selectedAudienceIds = this.audienceData.selectedAudiences.map(
        index => {
          return this.audienceData.audiences[index].id;
        }
      );

      // console.log("选中的观影人索引:", this.audienceData.selectedAudiences);
      // console.log("选中的观影人ID:", this.audienceData.selectedAudienceIds);

      // 关闭观影人选择弹窗
      this.showAudienceDialog = false;

      // 显示票数确认弹窗
      this.showConfirmDialog = true;
    }
  }
};
</script>

<style scoped>
.movie-detail-page {
  min-height: 100vh;
  background: #f5f5f5;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  padding-bottom: 80px; /* 为底部操作栏留出空间 */
}
/* 头部导航 */
.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: white;
  border-bottom: 1px solid #e0e0e0;
}

.nav-left,
.nav-right {
  width: 40px;
  z-index: 99;
}

.nav-center {
  flex: 1;
  text-align: center;
}

.page-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0 0 0 -40px;
}

.back-btn,
.share-btn {
  background: none;
  border: none;
  font-size: 18px;
  color: #333;
  cursor: pointer;
  padding: 8px;
}

/* 电影信息区域 */
.movie-info-section {
  display: flex;
  padding: 16px;
  background: white;
  gap: 12px;
}

.movie-poster {
  width: 100px;
  height: 140px;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
}

.movie-poster img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.movie-details {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.movie-title {
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.movie-meta {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  color: #666;
}

.separator {
  color: #ccc;
}

.director,
.cast {
  font-size: 14px;
  color: #666;
}

.label {
  color: #999;
  margin-right: 4px;
}

.value {
  color: #333;
}

/* 剧情简介 */
.synopsis-section {
  padding: 16px;
  background: white;
  margin-top: 8px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0 0 12px 0;
}

.synopsis-content {
  position: relative;
}

.synopsis-text {
  font-size: 14px;
  line-height: 1.6;
  color: #666;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 5;
  -webkit-box-orient: vertical;
  overflow: hidden;
  transition: all 0.3s ease;
}

.synopsis-text.expanded {
  display: block;
  -webkit-line-clamp: unset;
  overflow: visible;
}

.expand-btn {
  background: none;
  border: none;
  color: #ff4444;
  font-size: 14px;
  cursor: pointer;
  padding: 0;
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.expand-icon {
  font-size: 12px;
  transition: transform 0.3s ease;
}

.expand-icon.rotated {
  transform: rotate(180deg);
}

/* 选择场次 - 统一三端定位 */
.showtime-section {
  padding: 16px;
  background: white;
  margin-top: 8px;
}

.date-selector {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  overflow-x: auto;
  padding-bottom: 8px;
  -webkit-overflow-scrolling: touch;
}

.date-btn {
  background: #f5f5f5;
  border: none;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 14px;
  color: #666;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s;
}

.date-btn.active {
  background: #c34d34;
  color: white;
}

.showtime-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.showtime-item {
  display: flex;
  flex-direction: column;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 8px;
}

.showtime-main-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.showtime-info {
  flex: 1;
}

.session-name {
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
  border-bottom: 1px dashed #e0e0e0;
  text-align: center;
}

.time-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-items: flex-start;
}

.start-time {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.end-info {
  font-size: 14px;
  color: #666;
}

.remaining-tickets {
  font-size: 14px;
  color: #ff4444;
  font-weight: 500;
  text-align: center;
  min-width: 80px;
}

.ticket-selector {
  display: flex;
  align-items: center;
  gap: 12px;
}

.ticket-btn {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid #e0e0e0;
  background: white;
  color: #333;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.ticket-btn:hover {
  background: #f5f5f5;
}

.ticket-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.ticket-count {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  min-width: 20px;
  text-align: center;
}

/* 底部操作栏 */
.bottom-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  padding-bottom: calc(12px + env(safe-area-inset-bottom));
  background: white;
  border-top: 1px solid #e0e0e0;
  z-index: 100;
}

.total-info {
  flex: 1;
}

.total-text {
  font-size: 16px;
  color: #ff4444;
  font-weight: 600;
}

.confirm-btn {
  background: #c34d34;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.confirm-btn:hover {
  background: #c34d34;
}

.confirm-btn:disabled {
  background: #ccc;
  color: #999;
  cursor: not-allowed;
}

/* 空状态样式 */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #999;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.6;
}

.empty-text {
  font-size: 16px;
  color: #666;
}
/* 响应式设计 */
@media (max-width: 375px) {
  .movie-detail-page {
    padding-bottom: 70px; /* 小屏幕设备调整底部间距 */
  }

  .movie-info-section {
    padding: 12px;
  }

  .movie-poster {
    width: 80px;
    height: 112px;
  }

  .movie-title {
    font-size: 18px;
  }

  .synopsis-section,
  .showtime-section {
    padding: 12px;
  }

  .bottom-bar {
    padding: 10px 12px;
    padding-bottom: calc(10px + env(safe-area-inset-bottom));
  }
}

/* 大屏手机（常见 390~767） */
@media (min-width: 376px) and (max-width: 767px) {
  .movie-info-section {
    padding: 16px;
  }

  .movie-poster {
    width: 100px;
    height: 140px;
  }

  .movie-title {
    font-size: 20px;
  }

  .synopsis-section,
  .showtime-section {
    padding: 16px;
  }

  .bottom-bar {
    padding: 12px 16px;
    padding-bottom: calc(12px + env(safe-area-inset-bottom));
  }
}

@media (min-width: 768px) {
  .movie-detail-page {
    max-width: 768px;
    margin: 0 auto;
    padding-bottom: 90px; /* 平板设备调整底部间距 */
  }

  .movie-info-section {
    padding: 24px;
  }

  .movie-poster {
    width: 120px;
    height: 168px;
  }

  .movie-title {
    font-size: 24px;
  }

  .synopsis-section,
  .showtime-section {
    padding: 24px;
  }

  .bottom-bar {
    width: 100%;
    max-width: 768px;
    left: 50%;
    transform: translateX(-50%);
    padding: 16px 24px;
    padding-bottom: calc(16px + env(safe-area-inset-bottom));
  }
}

@media (min-width: 1024px) {
  .movie-detail-page {
    max-width: 1200px;
    margin: 0 auto;
    /* padding-bottom: 100px;  */
  }

  .movie-info-section {
    padding: 32px;
  }

  .movie-poster {
    width: 140px;
    height: 196px;
  }

  .movie-title {
    font-size: 28px;
  }

  .synopsis-section,
  .showtime-section {
    padding: 32px;
  }

  .bottom-bar {
    width: 100%;
    max-width: 1200px;
    left: 49.6%;
    transform: translateX(-50%);
    padding: 20px 32px;
    padding-bottom: calc(20px + env(safe-area-inset-bottom));
  }
}

/* 预约数不足弹窗样式 */
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.dialog-container {
  background: white;
  border-radius: 12px;
  max-width: 400px;
  width: 100%;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  animation: dialogSlideIn 0.3s ease-out;
}

@keyframes dialogSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 20px 0 20px;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 20px;
}

.dialog-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  color: #999;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;
}

.close-btn:hover {
  background: #f5f5f5;
  color: #666;
}

.dialog-content {
  padding: 0 20px 20px 20px;
  text-align: center;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.warning-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.dialog-message {
  font-size: 16px;
  color: #666;
  line-height: 1.5;
  margin: 0 0 20px 0;
}

.insufficient-info {
  background: #e3e4e6;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
}

.insufficient-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.insufficient-item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.insufficient-item:first-child {
  padding-top: 0;
}

.time-info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.date {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.time {
  font-size: 13px;
  color: #666;
}

.ticket-info {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  padding: 10px 0;
  border-bottom: 1px solid #c4c5c7;
}
.ticket-info-item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}
.ticket-info-msg {
  color: #ff4444;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
}
.selected {
  font-size: 13px;
  color: #ff4444;
  font-weight: 600;
}

.available {
  font-size: 12px;
  color: #999;
}

.dialog-footer {
  padding: 0 20px 20px 20px;
}

.dialog-btn {
  width: 100%;
  background: #c34d34;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 14px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.dialog-btn:hover {
  background: #a03d2a;
}

.dialog-btn:active {
  transform: translateY(1px);
}

/* 响应式弹窗样式 */
@media (max-width: 375px) {
  .dialog-overlay {
    padding: 16px;
  }

  .dialog-container {
    max-width: 100%;
  }

  .dialog-header {
    padding: 16px 16px 0 16px;
  }

  .dialog-content {
    padding: 0 16px 16px 16px;
    overflow-y: auto;
    flex: 1;
    min-height: 0;
  }

  .dialog-footer {
    padding: 0 16px 16px 16px;
  }

  .warning-icon {
    font-size: 40px;
  }

  .dialog-title {
    font-size: 16px;
  }

  .dialog-message {
    font-size: 14px;
  }

  .insufficient-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .ticket-info {
    align-items: flex-start;
  }
}

/* 页面加载组件样式 */
.page-loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}

.page-loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  padding: 40px 30px;
  min-width: 200px;
  text-align: center;
}

/* 旋转动画 */
.page-spinner {
  width: 50px;
  height: 50px;
  position: relative;
  margin-bottom: 20px;
}

.page-spinner-inner {
  width: 100%;
  height: 100%;
  border-top: 4px solid #fff;
  border-radius: 50%;
  animation: pageSpin 1s linear infinite;
}

@keyframes pageSpin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* 加载文字样式 */
.page-loading-text {
  font-size: 16px;
  color: #fff;
  font-weight: 500;
  margin-bottom: 10px;
}

.page-loading-dots {
  display: inline-block;
}

.page-dot {
  animation: pageDotPulse 1.4s infinite ease-in-out;
  animation-fill-mode: both;
}

.page-dot:nth-child(1) {
  animation-delay: -0.32s;
}

.page-dot:nth-child(2) {
  animation-delay: -0.16s;
}

.page-dot:nth-child(3) {
  animation-delay: 0s;
}

@keyframes pageDotPulse {
  0%,
  80%,
  100% {
    opacity: 0.3;
    transform: scale(0.8);
  }
  40% {
    opacity: 1;
    transform: scale(1);
  }
}

/* 加载提示文字 */
.page-loading-message {
  font-size: 14px;
  color: #fff;
  margin-top: 8px;
  max-width: 200px;
  line-height: 1.4;
}

/* 响应式设计 */
@media (max-width: 767px) {
  .page-loading-container {
    padding: 30px 20px;
    min-width: 160px;
    margin: 0 20px;
  }

  .page-spinner {
    width: 40px;
    height: 40px;
    margin-bottom: 15px;
  }

  .page-spinner-inner {
    border-width: 3px;
  }

  .page-loading-text {
    font-size: 14px;
  }

  .page-loading-message {
    font-size: 12px;
  }
}

@media (min-width: 768px) and (max-width: 1023px) {
  .page-loading-container {
    padding: 35px 25px;
  }

  .page-spinner {
    width: 45px;
    height: 45px;
  }
}

/* 深色主题支持 */
@media (prefers-color-scheme: dark) {
  .page-loading-container {
    background: rgba(30, 30, 30, 0.95);
    color: #fff;
  }

  .page-loading-text {
    color: #fff;
  }

  .page-loading-message {
    color: #ccc;
  }

  .page-spinner-inner {
    border-color: #444;
    border-top-color: #007bff;
  }
}

/* 确认选择弹窗样式 */
.confirm-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.confirm-info {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
}

.confirm-summary {
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e0e0e0;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.summary-item:last-child {
  margin-bottom: 0;
}

.summary-label {
  font-size: 14px;
  color: #666;
  font-weight: 500;
}

.summary-value {
  font-size: 14px;
  color: #333;
  font-weight: 600;
}

.selected-details {
  margin-top: 16px;
}

.details-title {
  font-size: 14px;
  color: #333;
  font-weight: 600;
  margin: 0 0 12px 0;
}

.date-group {
  margin-bottom: 12px;
}

.date-group:last-child {
  margin-bottom: 0;
}

.date-header {
  font-size: 14px;
  color: #c34d34;
  font-weight: 600;
  margin-bottom: 8px;
  padding: 4px 8px;
  background: #fff5f5;
  border-radius: 4px;
  border-left: 3px solid #c34d34;
}

.session-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.session-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: white;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
}

.session-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.session-name {
  font-size: 13px;
  color: #333;
  font-weight: 500;
}

.session-time {
  font-size: 12px;
  color: #666;
}

.ticket-count {
  font-size: 14px;
  color: #c34d34;
  font-weight: 600;
  background: #fff5f5;
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid #ffd6d6;
}

.dialog-footer {
  display: flex;
  gap: 12px;
  padding: 0 20px 20px 20px;
}

.dialog-btn.cancel {
  flex: 1;
  background: #f5f5f5;
  color: #666;
  border: 1px solid #e0e0e0;
}

.dialog-btn.cancel:hover {
  background: #e8e8e8;
  color: #333;
}

.dialog-btn.confirm {
  flex: 1;
  background: #c34d34;
  color: white;
  border: none;
}

.dialog-btn.confirm:hover {
  background: #a03d2a;
}

/* 响应式确认弹窗样式 */
@media (max-width: 375px) {
  .confirm-icon {
    font-size: 40px;
  }

  .confirm-info {
    padding: 12px;
  }

  .session-item {
    padding: 6px 10px;
  }

  .session-name {
    font-size: 12px;
  }

  .session-time {
    font-size: 11px;
  }

  .ticket-count {
    font-size: 12px;
    padding: 3px 6px;
  }

  .dialog-footer {
    padding: 0 16px 16px 16px;
    gap: 8px;
  }
}

/* 观影人信息选择弹窗样式 */
.audience-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.audience-list {
  max-height: 300px;
  overflow-y: auto;
  margin-bottom: 16px;
}

.audience-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.audience-item:hover {
  border-color: #c34d34;
  background: #fff5f5;
}

.audience-item.selected {
  border-color: #c34d34;
  background: #fff5f5;
}

.audience-item.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: #f5f5f5;
  border-color: #e0e0e0;
}

.audience-item.disabled:hover {
  border-color: #e0e0e0;
  background: #f5f5f5;
}

.audience-item:last-child {
  margin-bottom: 0;
}

.audience-info {
  flex: 1;
}

.audience-name {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 4px;
}

.audience-phone {
  font-size: 13px;
  color: #666;
}

.audience-item.disabled .audience-name {
  color: #999;
}

.audience-item.disabled .audience-phone {
  color: #ccc;
}

.audience-checkbox {
  width: 24px;
  height: 24px;
  border: 2px solid #e0e0e0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.audience-item.selected .audience-checkbox {
  border-color: #c34d34;
  background: #c34d34;
}

.audience-item.disabled .audience-checkbox {
  border-color: #ccc;
  background: #f5f5f5;
}

.check-icon {
  color: white;
  font-size: 14px;
  font-weight: bold;
}

.selection-hint {
  text-align: center;
  font-size: 14px;
  color: #666;
  padding: 8px;
  background: #f8f9fa;
  border-radius: 6px;
  margin-bottom: 16px;
  transition: all 0.3s ease;
}

.selection-hint.selection-complete {
  background: #e8f5e8;
  color: #2d5a2d;
  border: 1px solid #4caf50;
}

.complete-text {
  color: #4caf50;
  font-weight: 600;
  margin-left: 8px;
}

/* 响应式观影人弹窗样式 */
@media (max-width: 375px) {
  .audience-icon {
    font-size: 40px;
  }

  .audience-item {
    padding: 10px;
  }

  .audience-name {
    font-size: 13px;
  }

  .audience-phone {
    font-size: 12px;
  }

  .audience-checkbox {
    width: 20px;
    height: 20px;
  }

  .check-icon {
    font-size: 12px;
  }

  .selection-hint {
    font-size: 13px;
    padding: 6px;
  }
}
</style>
